import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCollection } from '@amzn/awsui-collection-hooks';
import Box from '@amzn/awsui-components-react/polaris/box';
import { PropertyFilterProps } from '@amzn/awsui-components-react';
import { FilterProperty } from '../attendance/wizard/tablePropertyFilter';
import { ColumnMap } from '../../hooks/tableColumns';
import Link from '@amzn/awsui-components-react/polaris/link';

export const INITIAL_SIGNUP_PAGE_SIZE = 100;

export enum SignupStatus {
  SIGNUP = 'Signed up',
  CANCEL = 'Signup Cancelled',
}

export interface AmazonSignup {
  signupId: string;
  name: string;
  alias: string;
  building: string;
  managerLogin: string;
  numberOfGuests: number;
  tShirtSize: string;
  signupStatus: SignupStatus;
}

// Common filter properties shared between shift and event level
export const baseSignupFilterProperties: FilterProperty[] = [
  { name: 'name', operators: [':'] },
  { name: 'alias', operators: [':'] },
  { name: 'building', operators: [':'] },
  { name: 'managerLogin', operators: [':'] },
  { name: 'numberOfGuests', operators: ['=', '>', '<'] },
  { name: 'tShirtSize', operators: ['='] },
  { name: 'signupStatus', operators: ['='] },
];

export function useSignupFilterProperties(
  additionalProperties: FilterProperty[] = []
): PropertyFilterProps.FilteringProperty[] {
  const { t } = useTranslation('signup');

  const allProperties = [...baseSignupFilterProperties, ...additionalProperties];

  return allProperties.map((property) => ({
    key: property.name,
    propertyLabel: t('propertyFilter.properties.' + property.name),
    groupValuesLabel: t('propertyFilter.values.' + property.name),
    operators: property.operators,
    defaultOperator: property.operators ? property.operators[0] : undefined,
  }));
}

export function useBaseSignupColumnMap(): ColumnMap<AmazonSignup> {
  return {
    name: (item) => item.name,
    alias: (item) => (
      <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.alias}`}>
        {item.alias}
      </Link>
    ),
    building: (item) => item.building,
    managerAlias: (item) => (
      <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.managerLogin}`}>
        {item.managerLogin}
      </Link>
    ),
    numberOfGuests: (item) => String(item.numberOfGuests),
    tShirtSize: (item) => item.tShirtSize,
    signupStatus: (item) => item.signupStatus,
  };
}

export function useSignupTableCollection(
  signups: AmazonSignup[],
  filteringProperties: PropertyFilterProps.FilteringProperty[],
  isError: boolean,
  pageSize: number
) {
  const { t } = useTranslation('signup');

  return useCollection(signups, {
    filtering: {
      empty: (
        <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
          <b>{isError ? t('signupSheet.errorOccurred') : t('signupSheet.noneFound')}</b>
        </Box>
      ),
    },
    propertyFiltering: {
      filteringProperties,
    },
    pagination: { pageSize },
    sorting: {},
  });
}
